import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError, filter, firstValueFrom, of, Subscription, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { faVoicemail } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import {
  IAppDashboard,
  IAppDashboardMenu,
  IDropDownMenuItem,
  IMenuItem,
  IPermissionsFlags,
  IPluginPermissions,
  IUser,
  ModelService,
  PluginInjectorApplicationPlugin,
  PluginInjectorService,
} from 'core-client/api';
import { VMailboxWidgetService } from './vmailbox-widget.service';
import { AsteriskVoicemail, Voicemessage } from '../../shared/services/db/asteriks-entities.interface';
import { VoiceMailboxStateUpdateItem } from '../utils/vmailbox-socket.constants';

interface BadgeTracker {
  widgetId: string;
  subscription: Subscription;
}

@Injectable({
  providedIn: 'root',
})
export class VmailboxMenuItemService implements OnDestroy {
  private readonly pluginModule = 'VMAILBOX';
  private readonly nameIdentifier = 'VMAILBOX';
  private badgeContext?: {
    dashboardId: string;
    mailboxes: AsteriskVoicemail[];
    dropdownItems: IDropDownMenuItem[];
    menuItem: IMenuItem;
    trackers: BadgeTracker[];
  };
  private dashboardSwitchSubscription?: Subscription;

  constructor(
    private readonly modelService: ModelService,
    private readonly pluginInjectorService: PluginInjectorService,
    private readonly vmailboxWidgetService: VMailboxWidgetService,
    private readonly translateService: TranslateService,
    private readonly ngZone: NgZone,
  ) {}

  /**
   * Registers the vmailbox navbar menu item synchronously.
   * Mailbox dropdown items are fetched and populated after the user authenticates.
   */
  getNavbarMenuItem(
    pluginConfig: PluginInjectorApplicationPlugin.IApplicationPlugin,
    dashboardMenu: IMenuItem[],
    primaryEnabled: boolean,
    secondaryEnabled: boolean,
  ): IMenuItem[] {
    const menuExist = dashboardMenu.some(
      (item: IMenuItem) =>
        item.nameIdentifier.toLowerCase() === this.nameIdentifier.toLowerCase(),
    );

    // Empty mutable array — populated post-login via observeUser$
    const dropdownItems: IDropDownMenuItem[] = [];

    const menuItem: IMenuItem = {
      pluginId: pluginConfig._id,
      pluginName: pluginConfig.name,
      pluginModule: this.pluginModule,
      nameIdentifier: this.nameIdentifier,
      accessRights: ['astimax_vmailbox_menuitem_enabled'],
      primary: {
        enabled: primaryEnabled,
        position: dashboardMenu.length + 1,
      },
      secondary: {
        enabled: secondaryEnabled,
        position: dashboardMenu.length + 1,
      },
      strictMode: false,
      icon: faVoicemail,
      dropdown: true,
      dropdownItems,
      badge$: new BehaviorSubject<number>(0),
      disabled: new BehaviorSubject(false),
    };

    if (!menuExist) {
      dashboardMenu.push(menuItem);
    }

    this.modelService._user.observeUser$().pipe(take(1)).subscribe((user) => {
      this.buildDropdownItems(pluginConfig, user)
        .then(({ items, mailboxes }) => {
          if (items.length === 0) {
            // No mailboxes assigned (or fetch failed) — push a disabled placeholder
            dropdownItems.push(this.createNoMailboxPlaceholder(pluginConfig.name));
          } else {
            dropdownItems.push(...items);
            const dashboardId = this.modelService._user.getUser()?.active_dashboard;
            if (dashboardId) {
              this.setupBadgeTracking(mailboxes, items, menuItem, dashboardId);

              this.dashboardSwitchSubscription = this.modelService._user.observeUser$().subscribe((updatedUser) => {
                const newDashboardId = updatedUser.active_dashboard;
                if (newDashboardId && this.badgeContext && newDashboardId !== this.badgeContext.dashboardId) {
                  console.log(`[VmailboxMenuItemService] Dashboard switched: ${this.badgeContext.dashboardId} → ${newDashboardId}`);
                  this.cleanupBadgeTracking();
                  this.setupBadgeTracking(this.badgeContext.mailboxes, this.badgeContext.dropdownItems, this.badgeContext.menuItem, newDashboardId);
                }
              });
            }
          }
        })
        .catch((err: unknown) => {
          console.warn('[VmailboxMenuItemService] Failed to load mailboxes:', err);
          dropdownItems.push(this.createNoMailboxPlaceholder(pluginConfig.name));
        });
    });

    return dashboardMenu;
  }

  private async buildDropdownItems(
    pluginConfig: PluginInjectorApplicationPlugin.IApplicationPlugin,
    user: IUser,
  ): Promise<{ items: IDropDownMenuItem[]; mailboxes: AsteriskVoicemail[] }> {
    const appDashboard = user.app_dashboards?.find(
      (d: IAppDashboard) => d._id === user.active_dashboard
    );

    const menuEntry = appDashboard?.menu.find(
      (m: IAppDashboardMenu) => m.nameIdentifier.toLowerCase() === this.nameIdentifier.toLowerCase(),
    );
    if (!menuEntry?.primary.enabled && !menuEntry?.secondary.enabled) {
      return { items: [], mailboxes: [] };
    }

    if (appDashboard && pluginConfig._id) {
      try {
        const pluginPermissions =
          await this.pluginInjectorService.permissionService.getPermissions(
            appDashboard,
            user,
          );

        const pluginAccess = pluginPermissions.find(
          (p: IPluginPermissions) => p.plugin_id === pluginConfig._id,
        );

        const menuitemPerms = pluginAccess?.menuitem_permissions?.[
          'vmailbox'
        ] as IPermissionsFlags | undefined;

        if (!menuitemPerms?.['astimax_vmailbox_menuitem_enabled']) {
          return { items: [], mailboxes: [] };
        }
      } catch {
        console.warn(
          '[VmailboxMenuItemService] Could not resolve permissions — skipping menu item API call.',
        );
        return { items: [], mailboxes: [] };
      }
    }

    let mailboxes: AsteriskVoicemail[] = [];

    try {
      mailboxes = await firstValueFrom(this.vmailboxWidgetService.getMailboxes());
    } catch {
      console.warn('[VmailboxMenuItemService] Could not fetch mailboxes for menu item.');
      return { items: [], mailboxes: [] };
    }

    const items = mailboxes.map((mailbox: AsteriskVoicemail) =>
      this.createMailboxDropdownItem(pluginConfig, mailbox),
    );
    return { items, mailboxes };
  }

  private createMailboxDropdownItem(
    pluginConfig: PluginInjectorApplicationPlugin.IApplicationPlugin,
    mailbox: AsteriskVoicemail,
  ): IDropDownMenuItem {
    // const prefix = this.translateService.instant('ASTIMAX.VMAILBOX.VMAILBOX');
    const mailboxLabel = `${mailbox.name}`;
    console.log(`[VmailboxMenuItemService] Creating dropdown item for mailbox ${mailbox.nr} with name="${mailbox.name}"`);

    const item: IDropDownMenuItem = {
      nameIdentifier: `MAILBOX_${mailbox.nr}`,
      pluginName: pluginConfig.name,
      pluginModule: this.pluginModule,
      label: mailboxLabel,
      strictMode: false,
      dropdown: false,
      dropdownItems: [],
      badge$: new BehaviorSubject<number>(0),
      disabled: new BehaviorSubject(false),
      callback: async (dialog: MatDialog) => {
        await this.openMailboxDialog(pluginConfig._id, mailbox.nr, item.label ?? mailboxLabel, dialog);
      },
    };

    return item;
  }

  /**
   * Returns a single disabled placeholder item used when the user has no voicemail
   * boxes assigned (or the fetch failed).
   */
  private createNoMailboxPlaceholder(pluginName: string): IDropDownMenuItem {
    return {
      nameIdentifier: 'NO_MAILBOX',
      pluginName,
      pluginModule: this.pluginModule,
      label: this.translateService.instant('ASTIMAX.VMAILBOX.MENU.NO_MAILBOX'),
      strictMode: false,
      dropdown: false,
      dropdownItems: [],
      // disabled=true: item is visible but greyed-out and not clickable.
      // No callback intentionally — clicking does nothing.
      disabled: new BehaviorSubject<boolean>(true),
    };
  }

  /**
   * Initialises badge tracking for all mailboxes.
   */
  private setupBadgeTracking(
    mailboxes: AsteriskVoicemail[],
    dropdownItems: IDropDownMenuItem[],
    menuItem: IMenuItem,
    dashboardId: string,
  ): void {
    // Guard against double-init: clean up any previous tracking
    this.cleanupBadgeTracking();

    this.badgeContext = {
      dashboardId,
      mailboxes,
      dropdownItems,
      menuItem,
      trackers: [],
    };

    const ctx = this.badgeContext;

    const updateMainBadge = () => {
      const total = dropdownItems.reduce(
        (sum: number, item: IDropDownMenuItem) => sum + ((item.badge$ as BehaviorSubject<number>)?.getValue() ?? 0),
        0,
      );
      (menuItem.badge$ as BehaviorSubject<number>).next(total);
    };

    mailboxes.forEach((mailbox, i) => {
      const dropdownItem = dropdownItems[i];
      const badge$ = dropdownItem.badge$ as BehaviorSubject<number>;
      const badgeWidgetId = `vmailbox-badge-${mailbox.nr}`;

      this.vmailboxWidgetService.initializeWidget(badgeWidgetId, dashboardId);
      this.vmailboxWidgetService.setLastKnownName(badgeWidgetId, mailbox.name ?? '');

      this.vmailboxWidgetService.getVoicemessages(mailbox.nr).pipe(
        take(1),
        catchError(() => of([] as Voicemessage[])),
      ).subscribe((msgs: Voicemessage[]) => {
        badge$.next(msgs.filter((m: Voicemessage) => m.isnew).length);
        updateMainBadge();
      });

      // Listener first — ensures no events are missed between trigger and subscribe
      const composite = new Subscription();
      composite.add(
        this.vmailboxWidgetService.onMessageCountUpdate(badgeWidgetId)
          .subscribe((update: VoiceMailboxStateUpdateItem) => {
            this.ngZone.run(() => {
              badge$.next(update.messages.new);
              updateMainBadge();
            });
          }),
      );

      // Trigger server-side subscription on each (re)connect
      composite.add(
        this.vmailboxWidgetService.getConnectionState().pipe(
          filter(state => state === 'connected'),
        ).subscribe(() => {
          this.vmailboxWidgetService.subscribeToMailboxCounts(mailbox.id, mailbox.nr, badgeWidgetId);
        }),
      );

      // Refresh badge count when a message is deleted externally
      composite.add(
        this.vmailboxWidgetService.onMessageDeleted(badgeWidgetId)
          .subscribe(({ messageId }) => {
            this.ngZone.run(() => {
              console.log(`[VmailboxMenuItemService] Message ${messageId} deleted — refreshing badge for ${mailbox.nr}`);
              this.vmailboxWidgetService.getVoicemessages(mailbox.nr).pipe(
                take(1),
                catchError(() => of([] as Voicemessage[])),
              ).subscribe((msgs: Voicemessage[]) => {
                badge$.next(msgs.filter((m: Voicemessage) => m.isnew).length);
                updateMainBadge();
              });
            });
          }),
      );

      // Update dropdown label when mailbox name changes
      composite.add(
        this.vmailboxWidgetService.onMailboxNameChanged(badgeWidgetId)
          .subscribe((newName: string) => {
            this.ngZone.run(() => {
              console.log(`[VmailboxMenuItemService] Mailbox name changed for ${mailbox.nr}: "${dropdownItem.label}" -> "${newName}"`);
              dropdownItem.label = newName;
            });
          }),
      );

      ctx.trackers.push({ widgetId: badgeWidgetId, subscription: composite });
    });
  }

  private cleanupBadgeTracking(): void {
    if (!this.badgeContext) return;
    this.badgeContext.trackers.forEach(({ widgetId, subscription }) => {
      subscription.unsubscribe();
      this.vmailboxWidgetService.cleanupWidget(widgetId);
    });
    this.badgeContext.trackers = [];
  }

  ngOnDestroy(): void {
    this.dashboardSwitchSubscription?.unsubscribe();
    this.cleanupBadgeTracking();
    this.badgeContext = undefined;
  }

  private async openMailboxDialog(
    pluginId: string,
    mailboxNr: string,
    mailboxLabel: string,
    dialog: MatDialog,
  ): Promise<void> {
    const { VmailboxNavbarMenuItemComponent } = await import(
      '../vmailbox-navbar-menu-item/vmailbox-navbar-menu-item.component'
    );
    const dialogConfig = this.pluginInjectorService.getMenuDialogConfig();
    const dialogRef = dialog.open(VmailboxNavbarMenuItemComponent, {
      ...dialogConfig,
      data: { pluginId, pluginModule: this.pluginModule, mailboxNr, mailboxLabel },
    });
    this.pluginInjectorService.setMenuDialogRef(dialogRef);
  }
}
